import { createRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, X } from 'lucide-react'
import { Route as rootRoute } from '../__root'

/**
 * Verify-email handler route — completes the verification round-trip.
 *
 * The transactional email contains a link of the shape
 * `…/auth/verify-email?userId=<guid>&token=<base64url>`. This route reads
 * those search params, calls the API once on mount, and renders one of three
 * states: `loading`, `success`, or `error`.
 *
 * On success we wait two seconds (long enough to read the confirmation) then
 * navigate to `/auth/sign-in`. On error we surface the failure and let the
 * user retry the sign-in flow manually — no auto-redirect, because we do not
 * want to mask a permanently broken link.
 *
 * The fetch is wired to an `AbortController` so React 18 / 19 strict-mode
 * double-invocation does not produce a redundant API call (the second
 * effect's cleanup aborts the first request).
 */
export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/verify-email',
  validateSearch: (search: Record<string, unknown>) => ({
    userId: String(search['userId'] ?? ''),
    token: String(search['token'] ?? ''),
  }),
  component: VerifyEmailPage,
})

type VerifyStatus = 'loading' | 'success' | 'error'

function VerifyEmailPage() {
  const { t } = useTranslation('auth')
  const { t: tCommon } = useTranslation('common')
  const { userId, token } = Route.useSearch()
  const navigate = useNavigate()
  const [status, setStatus] = useState<VerifyStatus>('loading')

  useEffect(() => {
    if (!userId || !token) {
      setStatus('error')
      return
    }

    const controller = new AbortController()
    let redirectTimer: ReturnType<typeof setTimeout> | undefined

    fetch(
      `/api/v1/auth/verify-email?userId=${encodeURIComponent(userId)}&token=${encodeURIComponent(token)}`,
      { signal: controller.signal },
    )
      .then((res) => {
        if (res.ok) {
          setStatus('success')
          redirectTimer = setTimeout(() => {
            void navigate({ to: '/auth/sign-in' })
          }, 2000)
        } else {
          setStatus('error')
        }
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') return
        setStatus('error')
      })

    return () => {
      controller.abort()
      if (redirectTimer !== undefined) clearTimeout(redirectTimer)
    }
  }, [userId, token, navigate])

  return (
    <main className="min-h-screen flex items-center justify-center bg-gradient-to-bl from-brand-50/60 via-white to-brand-100/30 px-4 py-12">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-3xl shadow-xl border border-brand-100 p-8 sm:p-10 text-center">
          <div
            className="inline-block h-1 w-12 rounded-full bg-brand-500 mb-4"
            aria-hidden="true"
          />

          {status === 'loading' ? (
            <>
              <div
                className="mx-auto mb-6 h-14 w-14 animate-spin rounded-full border-4 border-brand-100 border-t-brand-500"
                role="status"
                aria-live="polite"
              >
                <span className="sr-only">{tCommon('loading')}</span>
              </div>
              <h1 className="text-2xl font-bold font-display text-brand-900 tracking-tight mb-3">
                {t('verifyEmail')}
              </h1>
              <p className="text-brand-800 leading-relaxed">
                {tCommon('loading')}
              </p>
            </>
          ) : null}

          {status === 'success' ? (
            <>
              <div
                className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 ring-1 ring-emerald-200/70"
                aria-hidden="true"
              >
                <Check className="h-8 w-8 text-emerald-600" strokeWidth={2.5} />
              </div>
              <h1 className="text-2xl font-bold font-display text-emerald-700 tracking-tight mb-3">
                {t('verifyEmail')}
              </h1>
              <p className="text-brand-800 leading-relaxed">
                {tCommon('success')}
              </p>
            </>
          ) : null}

          {status === 'error' ? (
            <>
              <div
                className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-red-50 ring-1 ring-red-200/70"
                aria-hidden="true"
              >
                <X className="h-8 w-8 text-red-600" strokeWidth={2.5} />
              </div>
              <h1 className="text-2xl font-bold font-display text-red-700 tracking-tight mb-3">
                {tCommon('error')}
              </h1>
              <a
                href="/auth/sign-in"
                className="mt-2 inline-block font-semibold text-brand-600 hover:text-brand-700 hover:underline"
              >
                {t('signIn')}
              </a>
            </>
          ) : null}
        </div>
      </div>
    </main>
  )
}
